import { Loader2 } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { LANGUAGE_LABELS, LANGUAGE_OPTIONS } from '@/lib/languages'

function languageLabel(key: string): string {
  return Object.hasOwn(LANGUAGE_LABELS, key)
    ? LANGUAGE_LABELS[key as keyof typeof LANGUAGE_LABELS]
    : key
}

/**
 * Tabs for every language with a stored stub: known languages in dropdown
 * order, then anything else the column carries, so a stub is never hidden.
 */
function stubLanguages(starterCode?: Record<string, string>): string[] {
  const present = Object.keys(starterCode ?? {}).filter(
    (key) => starterCode?.[key],
  )
  const known: string[] = LANGUAGE_OPTIONS.map((option) => option.value)
  return [
    ...known.filter((key) => present.includes(key)),
    ...present.filter((key) => !known.includes(key)).sort(),
  ]
}

export function StarterCodeWidget({
  starterCode,
  preservedLanguages = [],
  onRegenerate,
  busy = false,
}: {
  starterCode?: Record<string, string>
  /** Languages whose authored stub the last save kept instead of replacing. */
  preservedLanguages?: readonly string[]
  /**
   * Re-save replacing every stub. The caller reports success and failure
   * (it owns the toast); the widget only closes the dialog either way.
   */
  onRegenerate?: () => Promise<void>
  /**
   * A save is already in flight. Both write the same config, so the trigger
   * is disabled rather than letting the two overlap and race for the
   * last-response-wins config the caller writes back into its form state.
   */
  busy?: boolean
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const keptLabels = preservedLanguages.map(languageLabel)

  const handleRegenerate = async () => {
    if (!onRegenerate) return
    setRegenerating(true)
    try {
      await onRegenerate()
    } catch {
      // Reported by the caller; a rejection must not leave the dialog stuck.
    } finally {
      setRegenerating(false)
      setConfirmOpen(false)
    }
  }

  const languages = stubLanguages(starterCode)
  const [selectedTab, setSelectedTab] = useState<string | null>(null)
  const baseId = useId()
  const panelId = `${baseId}-panel`
  const panelRef = useRef<HTMLPreElement>(null)
  // Python first when present: it is the stub every config has. Absent when
  // no stub is stored, which the type must say (indexed access is unchecked).
  const fallbackTab: string | undefined = languages.includes('python')
    ? 'python'
    : languages[0]
  const activeTab =
    selectedTab && languages.includes(selectedTab) ? selectedTab : fallbackTab

  const code = activeTab ? starterCode?.[activeTab] : undefined

  return (
    <div className="border-border min-w-0 flex-1 rounded-lg border">
      {/* Tabs */}
      <div className="border-border flex items-center gap-1 border-b px-2 pt-1.5">
        <span className="text-muted-foreground mr-2 text-xs font-medium">
          Starter Code
        </span>
        {languages.length > 0 ? (
          <div
            role="tablist"
            aria-label="Starter code language"
            className="flex items-center gap-1"
          >
            {languages.map((lang) => (
              <button
                key={lang}
                type="button"
                role="tab"
                id={`${baseId}-tab-${lang}`}
                aria-selected={activeTab === lang}
                // Only the active panel is in the DOM; aria-controls on the
                // other tabs would point at nothing.
                aria-controls={activeTab === lang ? panelId : undefined}
                onClick={() => setSelectedTab(lang)}
                className={`rounded-t px-2.5 py-1 text-xs font-medium transition-colors ${
                  activeTab === lang
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {languageLabel(lang)}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground py-1 text-xs italic">
            No languages
          </span>
        )}
      </div>
      {/* Code preview */}
      <div className="p-2">
        {code ? (
          <pre
            ref={panelRef}
            id={panelId}
            role="tabpanel"
            aria-labelledby={`${baseId}-tab-${activeTab}`}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: the stub overflows horizontally and has no focusable descendant — tabIndex lets a keyboard-only admin scroll it (WCAG 2.1.1 / axe scrollable-region-focusable).
            tabIndex={0}
            className="bg-muted/50 overflow-auto rounded p-2 text-xs leading-relaxed focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          >
            <code>{code}</code>
          </pre>
        ) : (
          <p className="text-muted-foreground p-2 text-xs italic">
            Save test configuration to generate starter code.
          </p>
        )}
      </div>
      {/* Kept-stub note (LG-370: a save never overwrites an authored stub) */}
      {preservedLanguages.length > 0 && (
        <div className="border-border flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
          <div className="text-muted-foreground text-xs">
            {preservedLanguages.map((lang) => (
              <p key={lang}>
                {languageLabel(lang)} stub kept: it was authored, not generated.
              </p>
            ))}
          </div>
          {onRegenerate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirmOpen(true)}
            >
              Regenerate starter code
            </Button>
          )}
        </div>
      )}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => !regenerating && setConfirmOpen(open)}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            // A successful regenerate leaves no kept stub, so the button that
            // opened this dialog is gone and focus would fall to the body.
            // Without a panel to take it, leave the restore to the dialog.
            const panel = panelRef.current
            if (preservedLanguages.length > 0 || !panel) return
            event.preventDefault()
            panel.focus()
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate starter code?</AlertDialogTitle>
            <AlertDialogDescription>
              This saves the test configuration and replaces every stub with the
              generator's output, including the authored {keptLabels.join(', ')}{' '}
              stub{keptLabels.length === 1 ? '' : 's'}. The authored code is not
              kept anywhere else.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={regenerating}
              onClick={(e) => {
                // Keep the dialog mounted until the save settles.
                e.preventDefault()
                void handleRegenerate()
              }}
            >
              {regenerating && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
